import React, { useEffect, useMemo, useState } from 'react';
import {
  Card,
  Form,
  Button,
  Spinner,
  Alert,
  Row,
  Col,
  Badge,
  InputGroup,
} from 'react-bootstrap';
import { BsCheckCircleFill, BsKey, BsSave } from 'react-icons/bs';
import api from '../lib/api';

const MODELS = ['mistral', 'tinyllama', 'openai', 'gemini', 'groq'];

function PanelBlock({ title, subtitle, children, className = '' }) {
  return (
    <Card
      className={`border-0 rounded-4 h-100 ${className}`}
      style={{ background: '#f8fafc' }}
    >
      <Card.Body className="p-3 p-md-4">
        <div className="fw-semibold mb-1" style={{ color: '#0f172a', fontSize: '1rem' }}>
          {title}
        </div>
        {subtitle ? (
          <div className="text-muted small mb-3" style={{ lineHeight: 1.55 }}>
            {subtitle}
          </div>
        ) : null}
        {children}
      </Card.Body>
    </Card>
  );
}

const AdminModelSelector = () => {
  const [currentModel, setCurrentModel] = useState('');
  const [openaiModel, setOpenaiModel] = useState('');
  const [geminiModel, setGeminiModel] = useState('');
  const [groqModel, setGroqModel] = useState('');

  const [openaiKey, setOpenaiKey] = useState('');
  const [geminiKey, setGeminiKey] = useState('');
  const [groqKey, setGroqKey] = useState('');

  const [hasOpenAI, setHasOpenAI] = useState(false);
  const [hasGemini, setHasGemini] = useState(false);
  const [hasGroq, setHasGroq] = useState(false);

  const [openaiMasked, setOpenaiMasked] = useState('');
  const [geminiMasked, setGeminiMasked] = useState('');
  const [groqMasked, setGroqMasked] = useState('');

  const [lastUpdated, setLastUpdated] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);

  const statusVariant = useMemo(
    () => (message.startsWith('Error') ? 'danger' : 'success'),
    [message]
  );

  const fetchConfig = async () => {
    try {
      setLoading(true);
      setMessage('');

      const res = await api.get('/chat-model/');
      const data = res.data || {};

      setCurrentModel(data.current_model || 'mistral');
      setOpenaiModel(data.openai_model || '');
      setGeminiModel(data.gemini_model || '');
      setGroqModel(data.groq_model || '');

      setHasOpenAI(!!data.has_openai_key);
      setHasGemini(!!data.has_gemini_key);
      setHasGroq(!!data.has_groq_key);

      setOpenaiMasked(data.openai_key_masked || '');
      setGeminiMasked(data.gemini_key_masked || '');
      setGroqMasked(data.groq_key_masked || '');

      setLastUpdated(data.updated_at || null);

      setOpenaiKey('');
      setGeminiKey('');
      setGroqKey('');
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.response?.data?.error ||
        err?.message ||
        'Failed to load model configuration.';
      setMessage(`Error: ${detail}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage('');

    try {
      const payload = {
        model: (currentModel || '').trim(),
        openai_model: (openaiModel || '').trim(),
        gemini_model: (geminiModel || '').trim(),
        groq_model: (groqModel || '').trim(),
      };

      if (openaiKey.trim()) payload.openai_key = openaiKey.trim();
      if (geminiKey.trim()) payload.gemini_key = geminiKey.trim();
      if (groqKey.trim()) payload.groq_key = groqKey.trim();

      const res = await api.post('/chat-model/', payload);
      const data = res.data || {};

      setHasOpenAI(!!data.has_openai_key);
      setHasGemini(!!data.has_gemini_key);
      setHasGroq(!!data.has_groq_key);

      if (typeof data.openai_key_masked === 'string') setOpenaiMasked(data.openai_key_masked);
      if (typeof data.gemini_key_masked === 'string') setGeminiMasked(data.gemini_key_masked);
      if (typeof data.groq_key_masked === 'string') setGroqMasked(data.groq_key_masked);

      if (typeof data.current_model === 'string') setCurrentModel(data.current_model);
      if (typeof data.openai_model === 'string') setOpenaiModel(data.openai_model);
      if (typeof data.gemini_model === 'string') setGeminiModel(data.gemini_model);
      if (typeof data.groq_model === 'string') setGroqModel(data.groq_model);

      setLastUpdated(data.updated_at || new Date().toISOString());
      setMessage(data.message || 'Configuration saved successfully.');

      setOpenaiKey('');
      setGeminiKey('');
      setGroqKey('');
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.response?.data?.error ||
        err?.message ||
        'Save failed';
      setMessage(`Error: ${detail}`);
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    fetchConfig();
  }, []);

  const KeyRow = ({ label, masked, has, value, onChange, placeholder }) => (
    <PanelBlock
      title={label}
      subtitle="Your current key remains hidden. Add a new one only when you want to replace the existing value."
    >
      <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
        <div className="small text-muted">Credential status</div>
        {has ? (
          <Badge bg="success" className="px-3 py-2 rounded-pill">
            <BsCheckCircleFill className="me-1" />
            Key set
          </Badge>
        ) : (
          <Badge bg="secondary" className="px-3 py-2 rounded-pill">
            Key not set
          </Badge>
        )}
      </div>

      <Row className="g-3">
        <Col md={7}>
          <Form.Group>
            <Form.Label>Current Key</Form.Label>
            <InputGroup>
              <InputGroup.Text>
                <BsKey />
              </InputGroup.Text>
              <Form.Control type="text" value={masked || ''} readOnly />
            </InputGroup>
          </Form.Group>
        </Col>

        <Col md={5}>
          <Form.Group>
            <Form.Label>New Key</Form.Label>
            <Form.Control
              type="password"
              value={value}
              onChange={(e) => onChange(e.target.value)}
              placeholder={placeholder}
            />
          </Form.Group>
        </Col>
      </Row>
    </PanelBlock>
  );

  if (loading) {
    return (
      <Card className="admin-surface-card h-100">
        <Card.Body className="text-center py-5">
          <Spinner animation="border" />
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="admin-surface-card h-100 border-0 shadow-sm rounded-4 overflow-hidden">
      <Card.Body className="p-4">
        <div className="mb-3">
          <div className="admin-section-title mb-1">AI Model Configuration</div>
          <p className="text-muted small mb-0" style={{ maxWidth: 720 }}>
            Choose the active provider.
          </p>
        </div>

        <Row className="g-4">
          <Col xs={12}>
            <PanelBlock
              title="Provider Selection"
              subtitle="Select your provider."
            >
              <Row className="g-3 align-items-end">
                <Col lg={6}>
                  <Form.Group>
                    <Form.Label>Active Model</Form.Label>
                    <Form.Select
                      value={currentModel}
                      onChange={(e) => setCurrentModel(e.target.value)}
                      disabled={saving}
                    >
                      {MODELS.map((m) => (
                        <option key={m} value={m}>
                          {m}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>

                {currentModel === 'openai' ? (
                  <Col lg={6}>
                    <Form.Group>
                      <Form.Label>OpenAI Model ID</Form.Label>
                      <InputGroup>
                        <Form.Control
                          type="text"
                          value={openaiModel}
                          onChange={(e) => setOpenaiModel(e.target.value)}
                          placeholder="Example: gpt-4.1-mini"
                        />
                        <InputGroup.Text>model</InputGroup.Text>
                      </InputGroup>
                    </Form.Group>
                  </Col>
                ) : null}

                {currentModel === 'gemini' ? (
                  <Col lg={6}>
                    <Form.Group>
                      <Form.Label>Gemini Model ID</Form.Label>
                      <InputGroup>
                        <Form.Control
                          type="text"
                          value={geminiModel}
                          onChange={(e) => setGeminiModel(e.target.value)}
                          placeholder="Example: gemini-1.5-pro"
                        />
                        <InputGroup.Text>model</InputGroup.Text>
                      </InputGroup>
                    </Form.Group>
                  </Col>
                ) : null}

                {currentModel === 'groq' ? (
                  <Col lg={6}>
                    <Form.Group>
                      <Form.Label>Groq Model ID</Form.Label>
                      <InputGroup>
                        <Form.Control
                          type="text"
                          value={groqModel}
                          onChange={(e) => setGroqModel(e.target.value)}
                          placeholder="Example: llama-3.1-8b-instant"
                        />
                        <InputGroup.Text>model</InputGroup.Text>
                      </InputGroup>
                    </Form.Group>
                  </Col>
                ) : null}

                {(currentModel === 'mistral' || currentModel === 'tinyllama') ? (
                  <Col xs={12}>
                    <div className="small text-muted">
                      No extra model ID or external API key is required for the currently selected provider in this panel.
                    </div>
                  </Col>
                ) : null}
              </Row>
            </PanelBlock>
          </Col>

          {currentModel === 'openai' ? (
            <Col xs={12}>
              <KeyRow
                label="OpenAI API Key"
                masked={openaiMasked}
                has={hasOpenAI}
                value={openaiKey}
                onChange={setOpenaiKey}
                placeholder={hasOpenAI ? 'Leave empty to keep current key' : 'Example: sk-...'}
              />
            </Col>
          ) : null}

          {currentModel === 'gemini' ? (
            <Col xs={12}>
              <KeyRow
                label="Gemini API Key"
                masked={geminiMasked}
                has={hasGemini}
                value={geminiKey}
                onChange={setGeminiKey}
                placeholder={hasGemini ? 'Leave empty to keep current key' : 'Example: AIza...'}
              />
            </Col>
          ) : null}

          {currentModel === 'groq' ? (
            <Col xs={12}>
              <KeyRow
                label="Groq API Key"
                masked={groqMasked}
                has={hasGroq}
                value={groqKey}
                onChange={setGroqKey}
                placeholder={hasGroq ? 'Leave empty to keep current key' : 'Example: gsk_...'}
              />
            </Col>
          ) : null}
        </Row>

        <div className="d-flex flex-wrap align-items-center gap-3 mt-4">
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={saving}
            className="admin-action-btn d-inline-flex align-items-center gap-2"
          >
            {saving ? <Spinner size="sm" animation="border" /> : <BsSave />}
            Save Configuration
          </Button>

          {lastUpdated ? (
            <span className="text-muted small">
              Last updated: {new Date(lastUpdated).toLocaleString()}
            </span>
          ) : null}
        </div>

        {message ? (
          <Alert variant={statusVariant} className="mt-3 mb-0 rounded-4">
            {message}
          </Alert>
        ) : null}
      </Card.Body>
    </Card>
  );
};

export default AdminModelSelector;